/**
 * DeepModePill — composer icon toggle for "Cerca a fondo" (Deep mode).
 *
 * May 26, 2026 — two-tier collapse. Replaces the
 * Quick / Standard / Expert ``SearchPowerSelector`` + the ``thinking_mode``
 * row inside ``ToolsMenu``. Jul 12, 2026 — icon-only, like the other composer
 * icon buttons (mic / scope): no label, active = highlighted icon on a soft
 * orange wash; the tooltip carries the name.
 *
 * Behaviour:
 *   - Controlled — caller owns ``active`` + ``onToggle`` so the state can
 *     persist on the per-conversation config slice.
 *   - ``enabled=false`` (plan-tier gate) and ``disabled=true`` (streaming)
 *     both render the disabled visual; the tooltip explains plan-gating
 *     when ``enabled=false``.
 *   - Framer Motion ``surface-in`` micro-bounce on activation; bypassed
 *     under ``prefers-reduced-motion``.
 *
 * A11y — emits ``role="switch"`` + ``aria-checked``; the ETA badge is
 * ``aria-hidden`` because the tooltip carries the same intent in prose.
 */

"use client";

import { motion } from "framer-motion";
import { Brain } from "lucide-react";
import type * as React from "react";

import { useReducedMotion } from "../../hooks/use-reduced-motion";
import { cn } from "../../lib/cn";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../primitives/tooltip";

export interface DeepModePillProps {
  /** True when "Cerca a fondo" is currently selected on the composer. */
  active: boolean;
  /** Fired with the next state. Caller owns persistence. */
  onToggle: (next: boolean) => void;
  /** Plan-tier gate — when false, the pill is visually disabled and the
   * tooltip explains the upgrade path. */
  enabled?: boolean;
  /** Imperative lock — e.g. while the run is streaming. */
  disabled?: boolean;
  className?: string;
}

// NOTE (May 26 2026): pre-existing design included an inline ``~30-60s`` ETA
// pill text + tooltip mention. Removed per UX feedback — Claude Extended
// Thinking and ChatGPT Deep Research don't surface a fixed ETA either:
// posting a number sets a brittle expectation (queries can run faster OR
// slower) and feels alarming. The activation pulse + the existing in-flight
// reasoning chain already communicate "this takes a moment".

export const DeepModePill: React.FC<DeepModePillProps> = ({
  active,
  onToggle,
  enabled = true,
  disabled = false,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const isDisabled = disabled || !enabled;
  // June 11, 2026 — copy update parallelo allo swap Telescope → Brain.
  // "ricerca approfondita" suonava come "scope deeper" (collisione
  // semantica con Esplora tutto / Ambito); ora la pill è esplicitamente
  // "ragionamento", coerente con SOTA peers post-o1 (ChatGPT Deep
  // Thinking, Claude Extended Thinking, Perplexity Pro Reasoning).
  const tooltipText = !enabled
    ? "Disponibile sui piani Pro+ — passa a Pro per attivare il ragionamento approfondito."
    : "Cerca a fondo: ragionamento esteso. Ando pensa più a lungo, " +
      "consulta più fonti e verifica le risposte. Usa più risorse, " +
      "le attese sono più lunghe.";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            type="button"
            role="switch"
            aria-checked={active}
            aria-label="Cerca a fondo"
            disabled={isDisabled}
            onClick={() => onToggle(!active)}
            initial={false}
            animate={reduceMotion ? undefined : active ? { scale: [1, 1.04, 1] } : { scale: 1 }}
            transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              // Icon-only toggle — matches the other composer icon buttons
              // (mic, scope): no label, active = highlighted icon on a soft
              // orange wash. The tooltip carries the name.
              "relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              "border-none bg-transparent transition-colors",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-ando-orange/40 focus-visible:ring-offset-1",
              active
                ? "bg-ando-orange-soft text-ando-orange"
                : "text-ink-3 hover:bg-black/[0.045] hover:text-ink-1",
              isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
              className,
            )}
          >
            <Brain size={14} strokeWidth={1.85} aria-hidden />
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" className="max-w-[220px] leading-snug">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
DeepModePill.displayName = "DeepModePill";
